import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {Field, reduxForm, formValueSelector, change} from 'redux-form';

import classNames from 'classnames';
import ErrorBlock from 'components/ErrorBlock';

import Label from 'components/Label';
import FormField from 'components/FormField';
import {
  CheckBoxField,
  TextAreaField,
  SelectField,
  SearchRoomField
} from 'components/Fields';
import {MoneyField} from 'components/Fields';
import {buildRoomSelectizeOption} from 'components/Fields/SearchRoomField';

import MultipleFileUpload from 'components/MultipleFileUpload';
import SingleFileUpload from 'components/SingleFileUpload';

import {selectCurrentUserPermissions} from 'ducks/auth';

import {
  fetchRoomOptionsForAddDepartureRoomCheck,
  MODALS,
  createDepartureRoomCheck
} from 'ducks/bookingSets';
import {headOption} from 'utils/common';

import {
  signDepartureRoomCheckImage as signDepartureRoomCheckImageSdk,
  signDepartureRoomCheckInventoryDocument as signDepartureRoomCheckInventoryDocumentSdk,
  signDepartureRoomCheckMaintenanceDocument as signDepartureRoomCheckMaintenanceDocumentSdk
} from 'sdk/bookingSets';

import './styles.css';
import {
  selectModal,
  closeModal,
  MODALS as BASE_MODALS,
  openNoPermissionModal
} from 'ducks/modals';

import {
  uploadFile,
  ACCEPTED_DOCUMENT_FILE_TYPES,
  ACCEPTED_IMAGE_FILE_TYPES
} from 'ducks/files';

import {hasEntityPermission} from 'utils/permissions';

import {prepareDataForPost} from 'components/CommonModals/components/OneOffChargeModal/components/AddInvoiceItemAndPaymentForm/utils';

import imageIcon from 'assets/icons/image-icon.svg';
import inventoryIcon from 'assets/icons/inventory-icon.svg';
import maintenanceIcon from 'assets/icons/maintenance-icon.svg';

import {PERMISSIONS} from 'ducks/auth';

const formSubmit = (values, dispatch, props) => {
  const {
    room,
    totalDamageCharge,
    description,
    imageIdentifiers,
    chargeFromDeposit,
    releaseFullDeposit,
    inventoryIdentifier,
    maintenanceIdentifier,
    paymentTakenAtFrontDesk
  } = values;
  const {bookingSetId} = props;
  const extraActions = props.getExtraActions();
  const failureExtraActions = [
    () => closeModal(BASE_MODALS.ONE_OFF_CHARGE),
    () => closeModal(MODALS.CONFIRM_ADD_DEPARTURE_ROOM_CHECK)
  ];

  const bookingId = _.get(room, 'object.bookingId');
  const departureRoomCheckData = {
    bookingSetId,
    booking: bookingId,
    totalDamageCharge,
    roomCheckDescription: description,
    imageIdentifiers,
    chargeFromDeposit,
    releaseFullDeposit,
    inventoryIdentifier,
    maintenanceIdentifier,
    paymentTakenAtFrontDesk
  };

  let data = departureRoomCheckData;

  if (paymentTakenAtFrontDesk) {
    const oneOffChargeData = prepareDataForPost({
      ...props.oneOffChargeFormData,
      bookingId
    });

    data = {
      ...data,
      ...oneOffChargeData
    };
  }

  dispatch(createDepartureRoomCheck(data, extraActions, failureExtraActions));
};

class AddDepartureRoomCheckForm extends React.Component<any> {
  state = {
    imageWrongFormat: false,
    inventoryWrongFormat: false,
    maintenanceWrongFormat: false,
    images: [],
    inventory: null,
    maintenance: null
  };

  componentDidMount() {
    const {bookingSetId} = this.props;

    const extraActions = this.props.storeDataExtraActions(data => ({
      roomOptions: data
    }));

    this.props.fetchRoomOptionsForAddDepartureRoomCheck(
      {bookingSetId},
      extraActions
    );
  }

  onRoomChange = selectedRoom => {
    if (_.isNil(selectedRoom)) {
      return this.props.change('roomType', null);
    }

    const roomTypeOption = {
      value: _.get(selectedRoom, 'object.roomType'),
      label: _.get(selectedRoom, 'object.roomType')
    };

    this.props.change('roomType', roomTypeOption);
  };

  onImageDrop = images => {
    if (_.isEmpty(images)) {
      return this.setState({imageWrongFormat: true});
    }

    this.setState({
      imageWrongFormat: false
    });

    let uploadedImage = headOption(images);

    // Store identifiers in form in order to be verified when the form is submitted
    const imageIdentifiers = this.props.imageIdentifiers;
    const storeIdentifierAction = identifier => {
      // Attach identifier in order to remove the identifier of the chosen image when being removed
      uploadedImage.identifier = identifier;
      const images = [...this.state.images, uploadedImage];
      this.setState({images});

      return change(
        'addDepartureRoomCheckForm',
        'imageIdentifiers',
        _.xor(imageIdentifiers, [identifier])
      );
    };

    const {bookingSetId} = this.props;
    const signData = {
      filename: uploadedImage.name,
      fileType: uploadedImage.type,
      bookingSetId
    };

    this.props.uploadFile({
      file: uploadedImage,
      signSdk: signDepartureRoomCheckImageSdk,
      signData,
      storeIdentifierAction
    });
  };

  onImageRemove = image => {
    this.setState({images: _.xor(this.state.images, [image])}, () =>
      this.props.change(
        'imageIdentifiers',
        _.xor(this.props.imageIdentifiers, [image.identifier])
      )
    );
  };

  onInventoryDrop = inventories => {
    if (_.isEmpty(inventories)) {
      return this.setState({inventoryWrongFormat: true});
    }

    const uploadedInventory = headOption(inventories);

    this.setState({inventory: uploadedInventory, inventoryWrongFormat: false});

    const storeIdentifierAction = identifier =>
      change('addDepartureRoomCheckForm', 'inventoryIdentifier', identifier);

    const {bookingSetId} = this.props;

    const signData = {
      filename: uploadedInventory.name,
      fileType: uploadedInventory.type,
      bookingSetId
    };

    this.props.uploadFile({
      file: uploadedInventory,
      signSdk: signDepartureRoomCheckInventoryDocumentSdk,
      signData,
      storeIdentifierAction
    });
  };

  onInventoryRemove = () => {
    this.setState({inventory: null}, () =>
      this.props.change('inventoryIdentifier', null)
    );
  };

  onMaintenanceDrop = maintenances => {
    if (_.isEmpty(maintenances)) {
      return this.setState({maintenanceWrongFormat: true});
    }

    const uploadedMaintenance = headOption(maintenances);

    this.setState({
      maintenance: uploadedMaintenance,
      maintenanceWrongFormat: false
    });

    const storeIdentifierAction = identifier =>
      change('addDepartureRoomCheckForm', 'maintenanceIdentifier', identifier);

    const {bookingSetId} = this.props;

    const signData = {
      filename: uploadedMaintenance.name,
      fileType: uploadedMaintenance.type,
      bookingSetId
    };

    this.props.uploadFile({
      file: uploadedMaintenance,
      signSdk: signDepartureRoomCheckMaintenanceDocumentSdk,
      signData,
      storeIdentifierAction
    });
  };

  onMaintenanceRemove = () => {
    this.setState({maintenance: null}, () =>
      this.props.change('maintenanceIdentifier', null)
    );
  };

  onIncludesDamagesChange = checked => {
    if (!checked) {
      this.props.change('totalDamageCharge', null);
    }
  };

  onReleaseFullDepositChange = event => {
    const newReleaseFullDeposit = event.target.checked;
    const {locationRef} = this.props;

    const hasReleaseFullDepositPermission = hasEntityPermission({
      permission: PERMISSIONS.RELEASE_FULL_DEPOSIT,
      locationRef,
      userPermissions: this.props.userPermissions
    });

    if (!hasReleaseFullDepositPermission) {
      event.preventDefault();
      this.props.openNoPermissionModal({});
    }

    if (newReleaseFullDeposit) {
      this.props.change('includesDamages', false);
      this.props.change('totalDamageCharge', null);
    }
  };

  onChargeFromDepositChange = event => {
    const {locationRef} = this.props;

    const hasReleaseFullDepositPermission = hasEntityPermission({
      permission: PERMISSIONS.RETAIN_FROM_DEPOSIT,
      locationRef,
      userPermissions: this.props.userPermissions
    });

    if (!hasReleaseFullDepositPermission) {
      event.preventDefault();
      this.props.openNoPermissionModal({});
    }
  };

  render() {
    const {
      className,
      error,
      roomOptions,
      includesDamagesValue,
      releaseFullDepositValue,
      chargeFromDepositValue
    } = this.props;

    const releaseFullDepositDisabled =
      includesDamagesValue || chargeFromDepositValue;

    return (
      <form className={classNames(className, 'AddDepartureRoomCheckForm')}>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <FormField>
          <Label>Room No</Label>
          <SearchRoomField rooms={roomOptions} onChange={this.onRoomChange} />
        </FormField>
        <FormField>
          <Field name="roomType" component={SelectField} disabled={true} />
        </FormField>
        <FormField className="includesDamages">
          <Field
            name="includesDamages"
            component={CheckBoxField}
            label="Includes damages?"
            onCheckBoxChange={this.onIncludesDamagesChange}
            disabled={releaseFullDepositValue}
          />
        </FormField>
        {includesDamagesValue && (
          <FormField>
            <Label>Damages charge total amount</Label>
            <Field name="totalDamageCharge" component={MoneyField} />
          </FormField>
        )}
        <FormField>
          <Field
            name="releaseFullDeposit"
            component={CheckBoxField}
            label="Release full deposit?"
            disabled={releaseFullDepositDisabled}
            onChange={this.onReleaseFullDepositChange}
          />
        </FormField>
        <FormField className="description">
          <Label>Description</Label>
          <Field
            name="description"
            component={TextAreaField}
            placeholder="Some notes here..."
          />
        </FormField>
        <FormField className="paymentTakenAtFrontDesk">
          <Field
            name="paymentTakenAtFrontDesk"
            label="Payment taken at Front Desk?"
            component={CheckBoxField}
          />
        </FormField>
        <FormField>
          <Field
            name="chargeFromDeposit"
            component={CheckBoxField}
            label="To be retained from deposit?"
            disabled={releaseFullDepositValue}
            onChange={this.onChargeFromDepositChange}
          />
        </FormField>
        <FormField>
          <MultipleFileUpload
            files={this.state.images}
            onFileDrop={this.onImageDrop}
            wrongFormat={this.state.imageWrongFormat}
            onFileRemove={this.onImageRemove}
            placeholderIcon={imageIcon}
            placeholderText="Upload images"
            acceptedFileTypes={ACCEPTED_IMAGE_FILE_TYPES}
            filesLabel="Images"
          />
        </FormField>
        <FormField>
          <SingleFileUpload
            file={this.state.inventory}
            onFileDrop={this.onInventoryDrop}
            wrongFormat={this.state.inventoryWrongFormat}
            onFileRemove={this.onInventoryRemove}
            acceptedFileTypes={ACCEPTED_DOCUMENT_FILE_TYPES}
            placeholderIcon={inventoryIcon}
            placeholderText="Upload inventory"
            fileLabel="Inventory"
          />
        </FormField>
        <FormField>
          <SingleFileUpload
            file={this.state.maintenance}
            onFileDrop={this.onMaintenanceDrop}
            wrongFormat={this.state.maintenanceWrongFormat}
            onFileRemove={this.onMaintenanceRemove}
            acceptedFileTypes={ACCEPTED_DOCUMENT_FILE_TYPES}
            placeholderIcon={maintenanceIcon}
            placeholderText="Upload maintenance report"
            fileLabel="Maintenance"
          />
        </FormField>
      </form>
    );
  }
}

const oneOffChargeFormSelector = formValueSelector(
  'createOneOffChargeInvoiceAndPayment'
);

const formSelector = formValueSelector('addDepartureRoomCheckForm');

const mapStateToProps = state => {
  const modal = selectModal(state, MODALS.ADD_DEPARTURE_ROOM_CHECK);

  const roomOptions = _.get(modal, 'data.roomOptions', []);

  const initialRoom = headOption(roomOptions, buildRoomSelectizeOption);
  const initialRoomType = {
    value: _.get(initialRoom, 'object.roomType'),
    label: _.get(initialRoom, 'object.roomType')
  };

  const imageIdentifiers = formSelector(state, 'imageIdentifiers');
  return {
    includesDamagesValue: formSelector(state, 'includesDamages'),
    releaseFullDepositValue: formSelector(state, 'releaseFullDeposit'),
    chargeFromDepositValue: formSelector(state, 'chargeFromDeposit'),
    initialValues: {
      room: initialRoom,
      roomType: initialRoomType,
      paymentTakenAtFrontDesk: false,
      includesDamages: false,
      releaseFullDeposit: false,
      chargeFromDeposit: false,
      imageIdentifiers: [],
      inventoryIdentifier: null,
      maintenanceIdentifier: null,
      totalDamageCharge: null
    },
    roomOptions,
    imageIdentifiers,
    oneOffChargeFormData: {
      oneOffChargeReason: oneOffChargeFormSelector(
        state,
        'oneOffChargeFormSelector'
      ),
      description: oneOffChargeFormSelector(state, 'description'),
      amount: oneOffChargeFormSelector(state, 'amount'),
      paymentMethod: oneOffChargeFormSelector(state, 'paymentMethod'),
      paymentDate: oneOffChargeFormSelector(state, 'paymentDate'),
      invoiceDueDate: oneOffChargeFormSelector(state, 'invoiceDueDate'),
      reference: oneOffChargeFormSelector(state, 'reference'),
      batchNumber: oneOffChargeFormSelector(state, 'batchNumber'),
      creditCardNumber: oneOffChargeFormSelector(state, 'creditCardNumber'),
      createPayment: oneOffChargeFormSelector(state, 'createPayment')
    },
    userPermissions: selectCurrentUserPermissions(state)
  };
};

const mapDispatchToProps = {
  fetchRoomOptionsForAddDepartureRoomCheck,
  uploadFile,
  change,
  openNoPermissionModal
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'addDepartureRoomCheckForm',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(AddDepartureRoomCheckForm)
) as React.ComponentType<any>;
